import { ChannelPlatform } from "../generated/gql/graphql";

/** Hosts that identify a named platform, matched against the parsed hostname
 * of a channel URL. Any other valid host is `other`. Shared so the browser and
 * the API agree on what counts as a channel link - the API is the authority,
 * the form is the fast feedback. */
export const platformHosts: Record<
  Exclude<ChannelPlatform, ChannelPlatform.Other>,
  string[]
> = {
  [ChannelPlatform.Youtube]: ["youtube.com", "youtu.be"],
  [ChannelPlatform.Facebook]: ["facebook.com", "fb.com"],
  [ChannelPlatform.Instagram]: ["instagram.com"],
  [ChannelPlatform.Tiktok]: ["tiktok.com"],
  [ChannelPlatform.X]: ["x.com", "twitter.com"],
};

/** The one host each platform's channels are stored under, so `fb.com`,
 * `m.facebook.com` and `facebook.com` links to the same account get the same
 * id. YouTube is left out: its id comes from the YouTube lookup, and
 * `youtu.be` links are videos that a different host would change. */
const canonicalHosts: Record<
  Exclude<ChannelPlatform, ChannelPlatform.Youtube | ChannelPlatform.Other>,
  string
> = {
  [ChannelPlatform.Facebook]: "facebook.com",
  [ChannelPlatform.Instagram]: "instagram.com",
  [ChannelPlatform.Tiktok]: "tiktok.com",
  [ChannelPlatform.X]: "x.com",
};

// TikTok share links point at a video, not an account, and their codes are
// case-sensitive, so they can't be stored as a channel.
const shareLinkHosts = ["vm.tiktok.com", "vt.tiktok.com"];

// Names a mistyped platform host is checked against. X is left out - one
// letter is too short to tell a typo from a real site.
const typoTargets: Record<
  string,
  Exclude<ChannelPlatform, ChannelPlatform.Other>
> = {
  youtube: ChannelPlatform.Youtube,
  facebook: ChannelPlatform.Facebook,
  instagram: ChannelPlatform.Instagram,
  tiktok: ChannelPlatform.Tiktok,
  twitter: ChannelPlatform.X,
};

/** Whether `a` is `b`, or `b` with one letter added, removed, changed or two
 * neighbouring letters swapped. */
const isWithinOneTypo = (a: string, b: string): boolean => {
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > 1) return false;

  if (a.length === b.length) {
    const diffs = [...a].flatMap((char, i) => (char === b[i] ? [] : [i]));
    if (diffs.length === 1) return true;

    const [first, second] = diffs;
    return (
      diffs.length === 2 &&
      second === first + 1 &&
      a[first] === b[second] &&
      a[second] === b[first]
    );
  }

  const [shorter, longer] = a.length < b.length ? [a, b] : [b, a];
  return [...longer].some(
    (_, i) => longer.slice(0, i) + longer.slice(i + 1) === shorter,
  );
};

// Case-insensitive: a pasted `HTTPS://` would otherwise get a second scheme
// prefixed onto it, which parses, and parses wrong.
const hasScheme = /^https?:\/\//i;

const parseUrl = (url: string): URL | null => {
  const value = url.trim();

  try {
    return new URL(hasScheme.test(value) ? value : `https://${value}`);
  } catch {
    return null;
  }
};

/** Decoded, non-empty path segments - empty when the url doesn't parse, throws on a malformed escape */
export const urlPathSegments = (url: string): string[] =>
  parseUrl(url)?.pathname.split("/").filter(Boolean).map(decodeURIComponent) ??
  [];

const hostMatches = (host: string, hosts: string[]): boolean =>
  hosts.some((known) => host === known || host.endsWith(`.${known}`));

/** Lowercased hostname without `www.`, or null when the url doesn't parse */
export const hostOf = (url: string): string | null =>
  parseUrl(url)
    ?.hostname.toLowerCase()
    .replace(/^www\./, "") ?? null;

// A dotted hostname ending in a real-looking TLD (letters, or punycode for
// non-Latin ones). Without it `https://example` or a bare word would pass.
const isPublicHost = (host: string): boolean =>
  /^([a-z0-9-]+\.)+([a-z]{2,}|xn--[a-z0-9-]+)$/.test(host);

const namedPlatformOf = (host: string): ChannelPlatform | null => {
  const match = Object.entries(platformHosts).find(([, hosts]) =>
    hostMatches(host, hosts),
  );
  return match ? (match[0] as ChannelPlatform) : null;
};

/** The platform a URL looks like a mistyped link to, e.g. YouTube for
 * `youtube.con` or `youtub.com`, or null when it isn't one. Without this a
 * typo would be accepted as a broken `other` channel. */
export const typoPlatformFromUrl = (
  url: string,
): Exclude<ChannelPlatform, ChannelPlatform.Other> | null => {
  const host = hostOf(url);
  if (!host || namedPlatformOf(host)) return null;

  // Every label but the TLD, so `youtube.co.uk` is caught as well.
  const labels = host.split(".").slice(0, -1);
  const match = Object.entries(typoTargets).find(([name]) =>
    labels.some((label) => isWithinOneTypo(label, name)),
  );

  return match ? match[1] : null;
};

/** The platform a URL belongs to, `other` for any host that isn't a named
 * platform, or null when the URL isn't a usable link or looks like a mistyped
 * platform link. Matches on the parsed hostname, so `m.youtube.com` resolves
 * to YouTube and `notyoutube.com` to `other`. */
export const platformFromUrl = (url: string): ChannelPlatform | null => {
  const host = hostOf(url);
  if (!host || !isPublicHost(host) || shareLinkHosts.includes(host))
    return null;

  const named = namedPlatformOf(host);
  if (named) return named;

  return typoPlatformFromUrl(url) ? null : ChannelPlatform.Other;
};

/** Whether a URL belongs to the platform it was submitted as. */
export const isUrlForPlatform = (
  url: string,
  platform: ChannelPlatform,
): boolean => platformFromUrl(url) === platform;

/** The URL a channel is stored under. For every platform but YouTube it's also
 * the channel id, so two links to the same account must give the same string
 * and links to different accounts must not.
 * - YouTube keeps its host and path case: `/channel/<id>` is case-sensitive.
 * - Facebook keeps `?id=` on `profile.php` links: it's the only part that
 *   tells unnamed profiles apart.
 * - Other sites keep their path case, port and query, since we can't know
 *   which parts identify the page. */
export const normalizeChannelUrl = (url: string): string => {
  const value = url.trim();
  const parsed = parseUrl(value);
  if (!parsed) return value.toLowerCase();

  const host = parsed.hostname.toLowerCase().replace(/^www\./, "");
  const path = parsed.pathname.replace(/\/+$/, "");

  const platform = platformFromUrl(value);

  switch (platform) {
    case null:
      return `https://${host}${path.toLowerCase()}`;

    case ChannelPlatform.Youtube:
      return `https://${host}${path}`;

    case ChannelPlatform.Other: {
      const port = parsed.port ? `:${parsed.port}` : "";
      return `https://${host}${port}${path}${parsed.search}`;
    }

    default: {
      const lowerPath = path.toLowerCase();
      const canonical = `https://${canonicalHosts[platform]}${lowerPath}`;
      const profileId = parsed.searchParams.get("id");

      return platform === ChannelPlatform.Facebook &&
        lowerPath === "/profile.php" &&
        profileId
        ? `${canonical}?id=${encodeURIComponent(profileId)}`
        : canonical;
    }
  }
};
